/**
 * Attachment content-key wrapping. A per-attachment AES-256-GCM content key
 * (raw 32 bytes) is ECIES-wrapped to the family X25519 public key so any
 * family member holding the family private key can decrypt the image bytes.
 * Mirrors the X25519 + HKDF + AES-GCM protocol of sharedItemCrypto, with a
 * distinct HKDF info label so a leak of one wrap never compromises another.
 * Shared so iOS/Android can match the byte format later. See spec §3.1.
 */

const HKDF_INFO = 'attachment-key-v1';

function bytesToBase64(b: Uint8Array): string {
  let s = '';
  for (const c of b) s += String.fromCharCode(c);
  return btoa(s);
}
function bytesToBase64Url(b: Uint8Array): string {
  return bytesToBase64(b).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
function base64UrlToBytes(b64url: string): Uint8Array {
  const pad = b64url.length % 4 === 0 ? '' : '='.repeat(4 - (b64url.length % 4));
  const std = (b64url + pad).replace(/-/g, '+').replace(/_/g, '/');
  return Uint8Array.from(atob(std), (c) => c.charCodeAt(0));
}

export interface WrappedContentKey {
  wrappedContentKey: string;   // base64url — AES-GCM ciphertext of the raw content key
  wrappedContentKeyIv: string; // base64url — 12-byte IV
  ephemeralPublicKey: string;  // base64url — X25519 ephemeral public key
}

export async function wrapAttachmentKey(
  rawKey: Uint8Array,
  familyPublicKey: Uint8Array,
): Promise<WrappedContentKey> {
  const ephKeyPair = (await crypto.subtle.generateKey({ name: 'X25519' }, true, ['deriveBits'])) as CryptoKeyPair;
  const ephPubJwk = await crypto.subtle.exportKey('jwk', ephKeyPair.publicKey);
  const ephPubBytes = base64UrlToBytes(ephPubJwk.x!);

  const familyPubKey = await crypto.subtle.importKey(
    'jwk',
    { kty: 'OKP', crv: 'X25519', x: bytesToBase64Url(familyPublicKey) },
    { name: 'X25519' },
    true,
    [],
  );
  const sharedSecret = await crypto.subtle.deriveBits({ name: 'X25519', public: familyPubKey }, ephKeyPair.privateKey, 256);

  const salt = new Uint8Array(64);
  salt.set(ephPubBytes, 0);
  salt.set(familyPublicKey, 32);
  const km = await crypto.subtle.importKey('raw', sharedSecret, 'HKDF', false, ['deriveKey']);
  const aesKey = await crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt, info: new TextEncoder().encode(HKDF_INFO) },
    km,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt'],
  );

  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, aesKey, rawKey as BufferSource));
  return {
    wrappedContentKey: bytesToBase64Url(ct),
    wrappedContentKeyIv: bytesToBase64Url(iv),
    ephemeralPublicKey: bytesToBase64Url(ephPubBytes),
  };
}

export async function unwrapAttachmentKey(
  wrapped: WrappedContentKey,
  familyPrivateKey: Uint8Array,
  familyPublicKey: Uint8Array,
): Promise<Uint8Array> {
  const familyPrivKey = await crypto.subtle.importKey(
    'jwk',
    {
      kty: 'OKP',
      crv: 'X25519',
      x: bytesToBase64Url(familyPublicKey),
      d: bytesToBase64Url(familyPrivateKey),
    } as JsonWebKey,
    { name: 'X25519' },
    false,
    ['deriveBits'],
  );
  const ephPubBytes = base64UrlToBytes(wrapped.ephemeralPublicKey);
  const ephPubKey = await crypto.subtle.importKey(
    'jwk',
    { kty: 'OKP', crv: 'X25519', x: wrapped.ephemeralPublicKey },
    { name: 'X25519' },
    true,
    [],
  );
  const sharedSecret = await crypto.subtle.deriveBits({ name: 'X25519', public: ephPubKey }, familyPrivKey, 256);

  const salt = new Uint8Array(64);
  salt.set(ephPubBytes, 0);
  salt.set(familyPublicKey, 32);
  const km = await crypto.subtle.importKey('raw', sharedSecret, 'HKDF', false, ['deriveKey']);
  const aesKey = await crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt, info: new TextEncoder().encode(HKDF_INFO) },
    km,
    { name: 'AES-GCM', length: 256 },
    false,
    ['decrypt'],
  );

  const iv = base64UrlToBytes(wrapped.wrappedContentKeyIv);
  const ct = base64UrlToBytes(wrapped.wrappedContentKey);
  return new Uint8Array(await crypto.subtle.decrypt({ name: 'AES-GCM', iv: iv.buffer as ArrayBuffer }, aesKey, ct.buffer as ArrayBuffer));
}
